import {
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  on,
  onCleanup,
  Show,
} from 'solid-js';
import { t, tPlural } from '../../i18n';
import { useCardMove } from '../../lib/card-move';
import { MACHINE_STATUS } from '../../lib/machine-labels';
import type { MovePreview, MovePreviewTarget, MoveRefusalWire } from '../../lib/move-calls';
import { standingRefusal } from '../../lib/move-copy';
import { PICK_FLASH_MS } from '../../lib/move-drag';
import type { MoveSubject } from '../../lib/move-items';
import type { MovePage } from '../../lib/move-page';
import {
  enterTarget,
  type MenuOption,
  menuOptions,
  SEARCH_AT,
  searchOptions,
  searchVisible,
} from '../../lib/move-to-menu';
import { previewConfirms } from '../../lib/move-work-panel';
import { orderStamp } from '../../lib/read-order';
import { prefersReducedMotion } from '../../lib/reduced-motion';
import { MoveOption } from './MoveOption';

// Move to (#6771, epic #6552; prototype menuHTML `:1901-1917`, the search `:1800-1847`, the
// `.mvmenu` rules `:966-976`): inline under its row or group header, "Move <label> to", one
// option per other machine of the org from the server's ONE preview (`lib/move-page.ts`),
// those that can take it first by lowest CPU, a refusing one `aria-disabled` with its
// sentence. At four or more machines a search over the name, the 8-char id, the server and
// the state word; ↓ goes to the first match, Enter moves to the first that can take it, Esc
// clears the field and a second Esc closes and returns focus to the grip. A pick flashes 260 ms
// (none under reduced motion), then ONE send through the page's flood guard.
//
// CLOSED, it is where a move's 429 waits (`cooling_down`, `source_budget`, `target_budget`,
// `transfers_in_flight`): the sentence under the row, its "N s" counting down, and the page
// NOT locked. Open, that wait shows on the option it was sent to.

type Option = MenuOption<MovePreviewTarget>;

/**
 * A Move to pick: the option flashes 260 ms (none under reduced motion), then ONE move through
 * the page guard. The flash's timeout is tracked: a menu closed inside it (Esc, another item,
 * the card leaving) drops it and sends nothing.
 */
function createPicker(
  page: MovePage | undefined,
  subject: () => MoveSubject,
  on: {
    readonly moved: () => void;
    readonly refused: (id: string, refusal: MoveRefusalWire) => void;
  },
) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  /** Bumped by every drop: a move answered after the menu closed changes nothing here. */
  let gen = 0;
  const drop = (): void => {
    gen += 1;
    if (timer === undefined) return;
    clearTimeout(timer);
    timer = undefined;
    page?.setFlash(null);
  };
  const pick = (o: Option): void => {
    // A second click inside the flash is ignored: one pick, one timer, one send.
    if (page === undefined || !o.canTake || timer !== undefined) return;
    // The control may have been disabled since the menu opened (a re-read spent the budget).
    const blocked = page.blockedWhy(subject().sourceId);
    if (blocked !== null) {
      page.announce(blocked);
      return;
    }
    const mine = gen;
    const send = (): void => {
      timer = undefined;
      page.setFlash(null);
      void page.move(subject(), o.machine.id).then((outcome) => {
        if (mine !== gen) return;
        if (outcome.kind === 'moved') {
          // The picked option leaves with the menu: focus goes back to the item's own grip (or
          // Move button) until a read shows the row on its new card, whose grip then takes it.
          page.closeMenu();
          on.moved();
        } else if (outcome.kind === 'refused') on.refused(o.machine.id, outcome.refusal);
      });
    };
    if (prefersReducedMotion()) {
      send();
      return;
    }
    page.setFlash(`${subject().key}>${o.machine.id}`);
    timer = setTimeout(send, PICK_FLASH_MS);
  };
  return { pick, drop };
}

/** Focus an item's own grip, or its Move button when it has none on screen. */
function focusItemControl(key: string): void {
  document
    .querySelector<HTMLElement>(`[data-move-grip="${key}"], [data-move-btn="${key}"]`)
    ?.focus();
}

const finePointer = (): boolean =>
  typeof globalThis.matchMedia === 'function' && globalThis.matchMedia('(pointer: fine)').matches;

/**
 * The search's keys (prototype `:1830-1847`): Esc clears a typed query (a second Esc reaches the
 * menu, which closes), ↓ goes to the first match, Enter moves to the first that can take it.
 */
function searchKey(
  e: KeyboardEvent,
  on: {
    readonly query: () => string;
    readonly clear: () => void;
    readonly first: () => HTMLElement | null;
    readonly enter: () => void;
  },
): void {
  if (e.key === 'Escape' && on.query() !== '') {
    e.preventDefault();
    e.stopPropagation();
    on.clear();
  } else if (e.key === 'ArrowDown') {
    const first = on.first();
    if (first !== null) {
      e.preventDefault();
      first.focus();
    }
  } else if (e.key === 'Enter') {
    e.preventDefault();
    on.enter();
  }
}

/** A pick the server refused: the refusal, and when it arrived (`orderStamp`). */
type PickRefusal = { readonly refusal: MoveRefusalWire; readonly order: number };

/**
 * Move to's options over the session's preview, asked at `order`. A refused pick disables its
 * machine until a preview asked AFTER the refusal confirms it (`previewConfirms`).
 */
function sessionOptions(
  page: MovePage,
  preview: MovePreview,
  order: number,
  refused: ReadonlyMap<string, PickRefusal>,
): Option[] {
  const stateWord = (id: string): string => {
    const box = page.boxOf(id);
    return box === undefined ? '' : t(MACHINE_STATUS[box.verdict.status]);
  };
  return menuOptions(preview.targets, page.boxOf, stateWord, (target) => {
    const now = page.clock();
    const sent = refused.get(target.runner_id);
    const superseded =
      sent !== undefined && order > sent.order && previewConfirms(preview, target.runner_id, now);
    const r =
      (superseded ? null : standingRefusal(sent?.refusal, now)) ??
      standingRefusal(preview.source.refusal, now) ??
      standingRefusal(target.refusal, now);
    return r === null ? null : page.textOf(r, target.cpu?.after ?? null);
  });
}

export function MoveToMenu(props: { readonly subject: MoveSubject }): JSX.Element {
  const m = useCardMove();
  const page = m?.page;
  const [q, setQ] = createSignal('');
  // A move refused since the preview: the refusal itself, never its sentence, so the sentence
  // reads the live clock and a timed one lapses when its `free_at` passes. Stamped as it
  // arrives (`orderStamp`): a session preview asked after it that confirms the machine
  // (`previewConfirms`) supersedes it (#6772 review round 8), as does closing the menu.
  const [refused, setRefused] = createSignal<ReadonlyMap<string, PickRefusal>>(new Map());
  let search: HTMLInputElement | undefined;
  let list: HTMLDivElement | undefined;
  const session = () => {
    const s = page?.session();
    return s?.subject.key === props.subject.key ? s : null;
  };
  const preview = () => session()?.preview ?? null;
  const options = createMemo((): Option[] => {
    const s = session();
    if (page === undefined || s === null || s.preview.status !== 'ready') return [];
    return sessionOptions(page, s.preview.preview, s.order, refused());
  });
  const shown = () => searchOptions(options(), q());
  const ready = (): MovePreview | null => {
    const p = preview();
    return p !== null && p.status === 'ready' ? p.preview : null;
  };
  const searchable = () => searchVisible(options().length, SEARCH_AT, q());
  const flashKey = (o: Option) => `${props.subject.key}>${o.machine.id}`;

  const focusGrip = (): void => focusItemControl(props.subject.key);
  const close = (): void => {
    page?.closeMenu();
    focusGrip();
  };
  const picker = createPicker(page, () => props.subject, {
    moved: focusGrip,
    refused: (id, refusal) => {
      setRefused((r) => new Map(r).set(id, { refusal, order: orderStamp() }));
      // Asked after the refusal: if it confirms the machine, it supersedes the refusal.
      void page?.refreshSession();
    },
  });
  onCleanup(picker.drop);
  const pick = picker.pick;

  const onSearchKey = (e: KeyboardEvent): void =>
    searchKey(e, {
      query: q,
      clear: () => setQ(''),
      first: () => list?.querySelector<HTMLButtonElement>('.move-opt') ?? null,
      enter: () => {
        const o = enterTarget(shown());
        if (o === null) page?.announce(t('fleet.move.nothing_takes'));
        else pick(o);
      },
    });
  const onInput = (value: string): void => {
    setQ(value);
    const n = shown().length;
    page?.announce(
      value.trim() === ''
        ? t('fleet.move.search_shown', { count: options().length })
        : tPlural('fleet.move.search_matches', n, { count: n }),
    );
  };
  const heading = () => t('fleet.move.menu_heading', { label: props.subject.label });
  // A memo, so the effect below runs only when the menu OPENS or CLOSES (#6772 review round 10).
  // As a plain accessor it re-ran on every session update (a preview landing, a fleet re-read)
  // and cleared the query and the refused picks each time.
  const open = createMemo((): boolean => page?.menuKey() === props.subject.key);
  const wait = () => page?.waitFor(props.subject.key) ?? null;
  createEffect(
    on(open, (o) => {
      if (!o) {
        picker.drop();
        return;
      }
      setQ('');
      setRefused(new Map());
      if (finePointer())
        queueMicrotask(() => {
          // The menu may have closed before this ran: focus only a field still on the page.
          if (search?.isConnected === true) search.focus({ preventScroll: true });
        });
    }),
  );
  return (
    <Show
      when={open()}
      fallback={
        <Show when={wait()}>
          {(w) => (
            <p class="move-wait" role="status">
              {w().text}
            </p>
          )}
        </Show>
      }
    >
      <fieldset
        class="move-menu"
        aria-label={heading()}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !e.defaultPrevented) {
            e.preventDefault();
            close();
          }
        }}
      >
        <p class="move-menu__head" aria-hidden="true">
          {heading()}
        </p>
        <Show when={searchable()}>
          <label class="move-menu__search">
            <input
              ref={search}
              type="search"
              class="move-menu__input"
              placeholder={t('fleet.move.search')}
              aria-label={t('fleet.move.search')}
              autocomplete="off"
              spellcheck={false}
              enterkeyhint="go"
              value={q()}
              onInput={(e) => onInput(e.currentTarget.value)}
              onKeyDown={onSearchKey}
            />
            <span class="move-menu__count" aria-hidden="true">
              {q().trim() === ''
                ? options().length
                : t('fleet.move.search_count', { shown: shown().length, total: options().length })}
            </span>
            <span class="move-menu__enter" aria-hidden="true">
              ↵
            </span>
          </label>
        </Show>
        <div ref={list} class="move-menu__list">
          <Show when={preview()?.status === 'pending'}>
            <p class="move-menu__note">{t('fleet.move.checking')}</p>
          </Show>
          <Show when={preview()?.status === 'failed'}>
            <p class="move-menu__note">{t('fleet.move.preview_failed')}</p>
          </Show>
          <Show when={preview()?.status === 'ready' && options().length === 0}>
            <p class="move-menu__note">{t('fleet.move.no_targets')}</p>
          </Show>
          <Show when={ready()}>
            {(p) => (
              // Keyed by machine id, looked up in the current list: a fleet re-read (or the
              // countdown's tick) builds new option objects, and iterating them would re-create
              // every button and drop a keyboard user's focus (`MachineJobs` keys likewise).
              <For each={shown().map((o) => o.machine.id)}>
                {(id) => (
                  <Show when={shown().find((o) => o.machine.id === id)}>
                    {(o) => (
                      <MoveOption
                        subject={props.subject}
                        option={o()}
                        preview={p()}
                        lit={page?.flash() === flashKey(o())}
                        onPick={() => pick(o())}
                      />
                    )}
                  </Show>
                )}
              </For>
            )}
          </Show>
          <Show when={searchable() && q().trim() !== '' && shown().length === 0}>
            <p class="move-menu__note">{t('fleet.move.search_none', { q: q().trim() })}</p>
          </Show>
        </div>
        <p class="move-menu__tip">{t('fleet.move.menu_tip')}</p>
      </fieldset>
    </Show>
  );
}
